"use client";

/**
 * Website — hold a room. Name, phone, optional email and requests; the
 * property's deposit rule and hold window come from its settings. Used by
 * the Rooms section on the storefront and by each room's own page.
 */

import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { CalendarDays, Check, Clock, Loader2, X } from "lucide-react";
import { daysBetween, formatDayRange } from "@/lib/config/accommodation";

const NAVY = "#0f172a";
const SLATE = "#64748b";
const money = (n: number, cur = "UGX") => `${cur} ${Math.round(n || 0).toLocaleString("en-US")}`;

export interface BookRoomDialogProps {
  brandId: string;
  storeId?: string;
  brandName: string;
  currency?: string;
  /** A room type row from `getAvailability` (needs _id, name, total, deposit). */
  room: any;
  checkInDay: number;
  checkOutDay: number;
  adults: number;
  children: number;
  settings: { checkInTime: string; checkOutTime: string; depositRule: any };
  onClose: () => void;
}

export function BookRoomDialog({
  brandId,
  storeId,
  brandName,
  currency = "UGX",
  room,
  checkInDay,
  checkOutDay,
  adults,
  children,
  settings,
  onClose,
}: BookRoomDialogProps) {
  const createOnlineHold = useMutation(api.accommodation.stays.createOnlineHold);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [requests, setRequests] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<any | null>(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const submit = async () => {
    setError(null);
    if (name.trim().length < 2) return setError("Please enter your full name.");
    if (phone.replace(/\D/g, "").length < 9) return setError("Please enter a phone number we can reach you on.");
    setSaving(true);
    try {
      const res = await createOnlineHold({
        brandId: brandId as Id<"brands">,
        storeId: storeId ? (storeId as Id<"stores">) : undefined,
        productId: room._id,
        checkInDay,
        checkOutDay,
        adults,
        children: children || undefined,
        guestName: name,
        guestPhone: phone,
        guestEmail: email.trim() || undefined,
        specialRequests: requests.trim() || undefined,
      });
      setDone(res);
    } catch (err: any) {
      const data = err?.data;
      setError(typeof data === "string" ? data : (err?.message || "").replace(/^.*Uncaught (?:ConvexError|Error):\s*/, "").split("\n")[0] || "Could not hold the room. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const nights = daysBetween(checkInDay, checkOutDay);
  const holdMins = done?.holdExpiresAt ? Math.max(1, Math.round((done.holdExpiresAt - Date.now()) / 60000)) : null;

  return (
    <>
      <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-[81] flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none">
        <div className="pointer-events-auto w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
          <div className="flex items-start justify-between gap-3 p-5 border-b" style={{ borderColor: "#e2e8f0" }}>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: SLATE }}>{done ? "Reserved" : "Book"}</p>
              <h3 className="text-lg font-black leading-tight" style={{ color: NAVY }}>{room.name}</h3>
              <p className="text-xs mt-0.5" style={{ color: SLATE }}>
                {formatDayRange(checkInDay, checkOutDay)} · {nights} night{nights === 1 ? "" : "s"} · {adults} adult{adults === 1 ? "" : "s"}{children ? `, ${children} child${children === 1 ? "" : "ren"}` : ""}
              </p>
            </div>
            <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-slate-100" aria-label="Close">
              <X className="w-5 h-5" style={{ color: SLATE }} />
            </button>
          </div>

          <div className="p-5 overflow-y-auto">
            {done ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-2xl p-4" style={{ backgroundColor: "rgba(34,197,94,0.1)" }}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white" style={{ backgroundColor: "#22c55e" }}><Check className="w-5 h-5" /></span>
                  <div>
                    <p className="text-sm font-bold" style={{ color: NAVY }}>Your room is held · {done.stayNumber}</p>
                    <p className="text-xs mt-0.5" style={{ color: SLATE }}>
                      {done.deposit > 0
                        ? `Pay the deposit of ${money(done.deposit, currency)} within ${holdMins && holdMins >= 60 ? `${Math.round(holdMins / 60)} hour${Math.round(holdMins / 60) === 1 ? "" : "s"}` : `${holdMins ?? 120} minutes`} to confirm it. We also sent this to your WhatsApp.`
                        : "No deposit needed — see you at check-in. We also sent this to your WhatsApp."}
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl p-4 text-sm space-y-1" style={{ border: "1px solid #e2e8f0" }}>
                  <div className="flex justify-between"><span style={{ color: SLATE }}>Room total</span><span className="font-bold" style={{ color: NAVY }}>{money(done.total, currency)}</span></div>
                  <div className="flex justify-between"><span style={{ color: SLATE }}>Deposit now</span><span className="font-bold" style={{ color: NAVY }}>{money(done.deposit, currency)}</span></div>
                  <div className="flex justify-between"><span style={{ color: SLATE }}>At check-out</span><span className="font-bold" style={{ color: NAVY }}>{money(Math.max(0, done.total - done.deposit), currency)}</span></div>
                  <p className="text-[11px] pt-1" style={{ color: SLATE }}>Check-in from {done.checkInTime} · check-out by {done.checkOutTime}</p>
                </div>
                <div className="flex flex-col gap-2">
                  {done.payUrl && (
                    <a href={done.payUrl} className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-white" style={{ backgroundColor: NAVY }}>
                      Pay the deposit · {money(done.deposit, currency)}
                    </a>
                  )}
                  <a href={done.stayUrl} className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold" style={{ border: "1px solid #e2e8f0", color: NAVY }}>
                    View your stay
                  </a>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl p-4 text-sm" style={{ backgroundColor: "#f8fafc" }}>
                  <div className="flex justify-between"><span style={{ color: SLATE }}>{nights} night{nights === 1 ? "" : "s"}</span><span className="font-bold" style={{ color: NAVY }}>{money(room.total, currency)}</span></div>
                  <div className="flex justify-between mt-1"><span style={{ color: SLATE }}>Deposit to hold the room</span><span className="font-bold" style={{ color: NAVY }}>{money(room.deposit, currency)}</span></div>
                  <p className="text-[11px] mt-2 flex items-center gap-1" style={{ color: SLATE }}><Clock className="w-3 h-3" /> Check-in from {settings.checkInTime} · check-out by {settings.checkOutTime}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block sm:col-span-2">
                    <span className="block text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: SLATE }}>Full name</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none focus:ring-2" style={{ borderColor: "#e2e8f0", color: NAVY }} placeholder="As on your ID" autoFocus />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: SLATE }}>WhatsApp / phone</span>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" className="w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none focus:ring-2" style={{ borderColor: "#e2e8f0", color: NAVY }} placeholder="+256 7…" />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: SLATE }}>Email (optional)</span>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" className="w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none focus:ring-2" style={{ borderColor: "#e2e8f0", color: NAVY }} placeholder="you@email.com" />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="block text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: SLATE }}>Anything we should know? (optional)</span>
                    <textarea rows={2} value={requests} onChange={(e) => setRequests(e.target.value)} className="w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none focus:ring-2" style={{ borderColor: "#e2e8f0", color: NAVY }} placeholder="Arriving late, ground floor, a cot…" />
                  </label>
                </div>
                {error && <p className="text-xs font-semibold" style={{ color: "#dc2626" }}>{error}</p>}
                <button
                  type="button"
                  onClick={submit}
                  disabled={saving}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: NAVY }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
                  Hold this room{room.deposit > 0 ? ` · deposit ${money(room.deposit, currency)}` : ""}
                </button>
                <p className="text-[11px] text-center" style={{ color: SLATE }}>
                  {brandName} will confirm on WhatsApp. {room.deposit > 0 ? "The room is held until the deposit is paid, then it is yours." : "No payment is taken online."}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default BookRoomDialog;
